import _ from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { EditState, ISurveyQuestionEditModel, IValidationResult, QuestionType, SourceType } from '../../models';
import { IQuestionQuery } from '../../models/IQuestionQuery';
import { ValidationResultHelper } from '../../utilities/ValidationHelper';
import { FormattedHeaderRow, FormattedTBodyContainer, FormattedTable, FormattedTableRow, FormattedTh } from '../common';
import { QuestionRow } from './QuestionRow';

export const FollowUpFormattedRow = styled(FormattedTableRow)`
	margin-left: 10px;
`;
FollowUpFormattedRow.displayName = 'FollowUpFormattedRow';

export interface ISurveyQuestionListTableProps {
	className?: string;
	includeDimension: boolean;
	isDemographic?: boolean;
	questions: ISurveyQuestionEditModel[];
	supportedCultures: string[];
	validationResult?: IValidationResult;
	openCustomModal: (query: IQuestionQuery) => void;
	openModal: (query: IQuestionQuery) => void;
	DeleteSurveyQuestion: (question: ISurveyQuestionEditModel) => void;
	ToggleQuestionIsRequired: (question: ISurveyQuestionEditModel, isRequired: boolean) => void;
	UpdateAllQuestionTypes: (questions: ISurveyQuestionEditModel[], questionType: QuestionType) => void;
}

export class SurveyQuestionListTable extends React.PureComponent<ISurveyQuestionListTableProps, { collapsedQuestions: number[] }> {
	public constructor(props: ISurveyQuestionListTableProps) {
		super(props);
		this.state = {
			collapsedQuestions: [],
		};

		this.toggleDrawer = this.toggleDrawer.bind(this);
	}

	public render() {
		const { className, isDemographic } = this.props;
		const questions = this.props.questions.filter((q: ISurveyQuestionEditModel) => q.editState !== EditState.Remove);
		const showFollowUpColumn = questions.some((q) => q.questionType === QuestionType.OpenEnded);
		return (
			<FormattedTable className={className ?? ''}>
				<FormattedHeaderRow>
					<FormattedTh flexBasis={40}>
						{this.props.includeDimension ? ContentConstants.TABLE_HEADER_DIMENSIONTEXT : ''}
						{ContentConstants.TABLE_HEADER_QUESTIONTEXT}
					</FormattedTh>
					<FormattedTh flexBasis={16}>{ContentConstants.TABLE_HEADER_RESPONSEOPTION}</FormattedTh>
					{!isDemographic && <FormattedTh flexBasis={23}>{ContentConstants.TABLE_HEADER_QUESTIONTYPE}</FormattedTh>}
					<FormattedTh flexBasis={13}>{ContentConstants.TABLE_HEADER_RESPONSE_REQUIRED}</FormattedTh>
					{showFollowUpColumn && <FormattedTh flexBasis={8}>{ContentConstants.TABLE_HEADER_FOLLOW_UP_QUESTION}</FormattedTh>}
					<FormattedTh flexBasis={8} rightAlign={true}>
						{ContentConstants.TABLE_HEADER_ACTIONS}
					</FormattedTh>
				</FormattedHeaderRow>
				<FormattedTBodyContainer>{this.getQuestionRows(showFollowUpColumn)}</FormattedTBodyContainer>
			</FormattedTable>
		);
	}

	private getQuestionRows(showFollowUpColumn: boolean) {
		const { questions, validationResult } = this.props;
		const validationHelper = new ValidationResultHelper(validationResult);
		// All questions without a parentQuestionId will be rendered.
		return questions
			.filter((q) => !q.parentQuestionId)
			.map((question: ISurveyQuestionEditModel) => (
				<>
					<FormattedTableRow id={question.id + ''} key={question.id}>
						<QuestionRow
							key={question.id}
							includeDimension={this.props.includeDimension}
							question={question}
							warnings={validationHelper.PropertyWarnings(question.surveyQuestionId)}
							errors={validationHelper.PropertyErrors(question.surveyQuestionId)}
							showBranching={false}
							showFollowUpColumn={showFollowUpColumn}
							isExpanded={
								questions.filter((q) => q.parentQuestionId === question.id).length > 0
									? !this.state.collapsedQuestions.includes(question.id)
									: undefined
							}
							isSummary={false}
							openModal={this.openModal}
							openCustomModal={this.openCustomModal}
							DeleteSurveyQuestion={this.props.DeleteSurveyQuestion}
							ToggleFollowUpQuestions={this.toggleDrawer}
							ToggleQuestionIsRequired={this.toggleQuestionIsRequired}
							UpdateAllQuestionTypes={this.props.UpdateAllQuestionTypes}
						/>
					</FormattedTableRow>
					{
						// Questions with a parentQuestionId will only be rendered if that parent has opened the follow-up drawer (expandedQuestions).
						!this.state.collapsedQuestions.includes(question.id) &&
							questions
								.filter((q) => q.parentQuestionId === question.id)
								.map((followUpQuestion: ISurveyQuestionEditModel) => (
									<FollowUpFormattedRow id={followUpQuestion.id + ''} key={followUpQuestion.id}>
										<QuestionRow
											key={followUpQuestion.id}
											disableIsRequired={!question.isRequired}
											errors={validationHelper.PropertyErrors(followUpQuestion.surveyQuestionId)}
											includeDimension={this.props.includeDimension}
											isSummary={false}
											question={followUpQuestion}
											showBranching={false}
											showFollowUpColumn={showFollowUpColumn}
											warnings={validationHelper.PropertyWarnings(followUpQuestion.surveyQuestionId)}
											openModal={this.openModal}
											openCustomModal={this.openCustomModal}
											DeleteSurveyQuestion={this.props.DeleteSurveyQuestion}
											ToggleQuestionIsRequired={this.toggleQuestionIsRequired}
											UpdateAllQuestionTypes={this.props.UpdateAllQuestionTypes}
										/>
									</FollowUpFormattedRow>
								))
					}
				</>
			));
	}

	private getSelectedQuestionVariationIds() {
		return this.props.questions
			.filter((q: ISurveyQuestionEditModel) => q.sourceType === SourceType.Library && q.sourceQuestionVariationId !== undefined)
			.map((q: ISurveyQuestionEditModel) => q.sourceQuestionVariationId || 0);
	}

	private getSelectedCustomQuestionIds() {
		return this.props.questions
			.filter((q: ISurveyQuestionEditModel) => q.sourceType === SourceType.Custom)
			.map((q: ISurveyQuestionEditModel) => q.surveyQuestionId || 0);
	}

	private openCustomModal = (parentQuestion: ISurveyQuestionEditModel) => {
		const { isDemographic } = this.props;
		const query: IQuestionQuery = {
			isDemographic: isDemographic ?? false,
			parentQuestion: parentQuestion,
			selectedQuestionVariationIds: this.getSelectedQuestionVariationIds(),
			selectedCustomQuestionIds: this.getSelectedCustomQuestionIds(),
		};
		this.props.openCustomModal(query);
	};

	private openModal = (parentQuestion: ISurveyQuestionEditModel) => {
		const { isDemographic } = this.props;
		const query: IQuestionQuery = {
			isDemographic: isDemographic ?? false,
			parentQuestion: parentQuestion,
			selectedQuestionVariationIds: this.getSelectedQuestionVariationIds(),
			selectedCustomQuestionIds: this.getSelectedCustomQuestionIds(),
		};
		this.props.openModal(query);
	};

	private toggleDrawer(questionId: number) {
		const { collapsedQuestions } = this.state;
		if (collapsedQuestions.includes(questionId)) {
			this.setState({ collapsedQuestions: collapsedQuestions.filter((id) => id !== questionId) });
		} else {
			this.setState({ collapsedQuestions: [...collapsedQuestions, questionId] });
		}
	}

	// Follow-up questions should not be required if their parent is not required.
	private toggleQuestionIsRequired = (question: ISurveyQuestionEditModel, value: boolean) => {
		if (value === false) {
			const followUpQuestions = this.props.questions.filter((q) => q.parentQuestionId === question.id);
			_.forEach(followUpQuestions, (followUpQuestion) => {
				this.props.ToggleQuestionIsRequired(followUpQuestion, value);
			});
		}
		this.props.ToggleQuestionIsRequired(question, value);
	};
}
